import { Stack, useTheme } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BoxForm, Select, Switch, TextField } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import {
  kWordsAvailabilities,
  KWordsAvailability,
} from 'features/inventory/domain/types/kWordsAvailability'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { useGetWorkspaceDefaults, WorkspaceDefaults } from './useGetWorkspaceDefaults'
import { kWordsAvailabilityColor } from './utils'
import { EmployeeSettingsWorkspaceDefaults } from './WorkspaceDefaults/EmployeeSettingsWorkspaceDefaults'

import type { Schema } from './schema'
import type { SelectOption } from 'components/Select/types'

export const EmployeeSettingsSection = (): React.JSX.Element => {
  const theme = useTheme()
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.richPlacements.kWords.employeeSettings',
  })
  const { control, watch } = useFormContext<Schema>()
  const { overrideWorkspaceDefaults } = watch()

  const workspaceDefaults: undefined | WorkspaceDefaults = useGetWorkspaceDefaults()

  const ability = useAbility()
  const cannotUpdate: boolean =
    ability.cannot('update', 'Domain') ||
    ability.cannot('update', 'DomainRichPlacementsFeature-kWordsEmployeeSettings')

  return (
    <Paper
      data-cy='section-employee-settings'
      variant='cream'
    >
      <BoxForm>
        <SectionHeader
          subtitle={t('subtitle')}
          title={t('title')}
        />

        <Select<Schema, KWordsAvailability>
          control={control}
          disabled={cannotUpdate}
          label={t('availability.label')}
          labelColor={({ value }) => kWordsAvailabilityColor({ availability: value, theme })}
          name='availability'
          options={kWordsAvailabilities.map(
            (availability: KWordsAvailability): SelectOption<KWordsAvailability> => ({
              label: t(`availability.options.${availability}`),
              value: availability,
            }),
          )}
          tooltip={{ content: t('availability.tooltip') }}
        />

        <Stack
          direction='row'
          spacing={CONTAINER_SPACING}
        >
          <BoxForm>
            <Switch
              control={control}
              disabled={cannotUpdate}
              label={t('overrideWorkspaceDefaults.label')}
              labelPlacement='start'
              name='overrideWorkspaceDefaults'
              tooltip={{ content: t('overrideWorkspaceDefaults.tooltip') }}
            />

            {overrideWorkspaceDefaults && (
              <TextField
                control={control}
                disabled={cannotUpdate}
                label={t('kwordsSiteId.label')}
                name='kwordsSiteId'
                tooltip={{ content: t('kwordsSiteId.tooltip') }}
              />
            )}
          </BoxForm>

          {overrideWorkspaceDefaults && workspaceDefaults && (
            <EmployeeSettingsWorkspaceDefaults workspaceDefaults={workspaceDefaults} />
          )}
        </Stack>
      </BoxForm>
    </Paper>
  )
}
